import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trpc } from '~/api/trpc';
import { Loader2 } from 'lucide-react';
import { useUserStore } from '~/stores/user-store';
import BillingDraftBasicDetailsForm from './BillingDraftBasicDetailsForm';
import BillingDraftLineItemsEditor, { type DiscountDefinition } from './BillingDraftLineItemsEditor';
import type { BillingDraftFormData, BillingDraftLineItem } from './BillingDraftCreator';

// Re-use the same schemas from BillingDraftCreator
const lineItemSchema = z.object({
    id: z.string().optional(),
    description: z.string().min(1, 'Beschreibung erforderlich'),
    sequence: z.number().int(),
    servicePillarType: z.enum(['AESTHETIC', 'COSMETIC', 'HEALTH']),
    serviceMedicalType: z.enum(['MEDICAL', 'NOT_MEDICAL']),
    sourceType: z.enum(['ORIGINATED_FROM_CUSTOMER_BOOKED_CART', 'UPSELL', 'PHYSICAL_PRODUCT', 'BUNDLE_FIRST_CHARGE']),
    totalGross: z.number(),
    totalGrossDiscounted: z.number(),
    grossDiscountAmount: z.number(),
    vatRate: z.number(),
    totalNet: z.number(),
    totalNetDiscounted: z.number(),
    netDiscountAmount: z.number(),
    vatAmount: z.number(),
    vatAmountDiscounted: z.number(),
    treatmentVariantId: z.string().nullable().optional(),
    physicalProductId: z.string().nullable().optional(),
    bookableItemId: z.string().nullable().optional(),
    bookableItemAddonId: z.string().nullable().optional(),
    cartItemSourceId: z.string().nullable().optional(),
    bundleId: z.string().nullable().optional(),
    consumeFirstCharge: z.boolean().optional(),
    paidByBundleVoucherId: z.string().nullable().optional(),
    originalPriceGross: z.number().optional(),
    giftCardTemplateId: z.string().nullable().optional(),
    giftCardRecipientCustomerId: z.number().int().nullable().optional(),
    discounts: z.array(z.object({
        discountCodeId: z.string().nullable().optional(),
        label: z.string(),
        discountAmountEur: z.number(),
        note: z.string().nullable().optional(),
    })).optional().default([]),
}).refine(
    (data) => data.treatmentVariantId || data.physicalProductId || data.giftCardTemplateId,
    { message: 'Entweder Behandlungsvariante, Produkt oder Gutscheinkarte erforderlich' }
);

const billingDraftFormSchema = z.object({
    customerId: z.number().int().min(1, 'Kunde erforderlich'),
    appointmentId: z.string().nullable(),
    storeId: z.number().int().min(1, 'Filiale erforderlich'),
    treatmentDate: z.string().min(1, 'Behandlungsdatum erforderlich'),
    billingDraftDate: z.string().min(1, 'Entwurfsdatum erforderlich'),
    dueDate: z.string().min(1, 'Fälligkeitsdatum erforderlich'),
    type: z.enum(['NORMAL', 'INFLUENCER', 'MARKETING', 'ACADEMY', 'KULANZ']),
    influencerType: z.enum(['BEAUTY_INSIDER', 'INFLUENCER', 'INFLUENCER_FLATRATE']).nullable().optional(),
    status: z.enum(['PENDING_PAYMENT', 'PAID', 'CANCELLED']),
    companyName: z.string().min(1, 'Firmenname erforderlich'),
    companyAddress: z.string().min(1, 'Adresse erforderlich'),
    companyCity: z.string().min(1, 'Stadt erforderlich'),
    companyCountry: z.string().min(1, 'Land erforderlich'),
    customerName: z.string().min(1, 'Kundenname erforderlich'),
    customerNumber: z.string(),
    customerStreet: z.string(),
    customerZipcode: z.string(),
    customerCity: z.string(),
    customerCountry: z.string(),
    cartSourceId: z.string().nullable().optional(),
    lineItems: z.array(lineItemSchema).min(1, 'Mindestens eine Position erforderlich'),
});

// Type for the billing draft data from getOne
// Uses `any` for numeric fields because Prisma Decimals serialize as strings via tRPC
type BillingDraftData = {
    id: string;
    customerId: number;
    appointmentId: string | null;
    storeId: number;
    treatmentDate: string | Date;
    billingDraftDate: string | Date;
    dueDate: string | Date;
    type: string;
    status: string;
    companyName: string;
    companyAddress: string;
    companyCity: string;
    companyCountry: string;
    customerName: string;
    customerNumber: string;
    customerStreet: string;
    customerZipcode: string;
    customerCity: string;
    customerCountry: string;
    cartSourceId?: string | null;
    createdById: number;
    createdBy?: {
        id: number;
        firstName: string;
        lastName: string;
    };
    totalGross: any;
    totalGrossDiscounted: any;
    totalNet: any;
    totalNetDiscounted: any;
    vatAmount: any;
    vatAmountDiscounted: any;
    lineItems: Array<{
        id: string;
        description: string;
        sequence: number;
        servicePillarType: string;
        serviceMedicalType: string;
        sourceType: string;
        totalGross: any;
        totalGrossDiscounted: any;
        grossDiscountAmount: any;
        vatRate: any;
        totalNet: any;
        totalNetDiscounted: any;
        netDiscountAmount: any;
        vatAmount: any;
        vatAmountDiscounted: any;
        treatmentVariantId?: string | null;
        physicalProductId?: string | null;
        bookableItemId?: string | null;
        bookableItemAddonId?: string | null;
        cartItemSourceId?: string | null;
        bundleId?: string | null;
        consumeFirstCharge?: boolean;
        bundle?: any;
        giftCardTemplateId?: string | null;
        giftCardRecipientCustomerId?: number | null;
        discounts?: Array<{
            id: string;
            discountAmountEur: any;
            discountCodeId?: string | null;
            note?: string | null;
            discountCode?: {
                id: string;
                name: string;
                type: string;
                discountValue: any;
            } | null;
        }>;
    }>;
    [key: string]: any;
};

type BillingDraftEditorProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    billingDraft: BillingDraftData;
    onSuccess: () => void;
};

const toNum = (val: number | string | { toNumber?: () => number } | any): number => {
    if (typeof val === 'number') { return val; }
    if (typeof val === 'string') { return parseFloat(val) || 0; }
    if (val && typeof val.toNumber === 'function') { return val.toNumber(); }
    return Number(val) || 0;
};

const formatDateStr = (date: string | Date): string => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
};

const BillingDraftEditor = ({
    open,
    onOpenChange,
    billingDraft,
    onSuccess,
}: BillingDraftEditorProps) => {
    const [activeTab, setActiveTab] = useState('lineItems');
    const trpcUtils = trpc.useUtils();
    const currentUser = useUserStore((state) => state.user);
    const [selectedCreatorUserId, setSelectedCreatorUserId] = useState<number>(currentUser?.id ?? 0);

    const { data: usersData } = trpc.user.getAll.useQuery({ page: 0, pageSize: 100 });

    // Fetch appointment details to get coupon codes
    const { data: appointmentData } = trpc.appointments.getOne.useQuery(
        { id: billingDraft.appointmentId || '' },
        { enabled: !!billingDraft.appointmentId }
    );

    // Build line items with BASE prices (pre-discount) so the editor can recalculate
    const { mappedLineItems, initialDiscountDefs } = useMemo(() => {
        const defs: DiscountDefinition[] = [];
        const discountCodeMap = new Map<string, {
            discountCodeId: string;
            label: string;
            type: string;
            value: number;
            totalAmount: number;
        }>();
        // Manual discounts are keyed by their (optional) linked code id + note so that
        // individual notes and code links survive an edit round-trip.
        const manualDiscountMap = new Map<string, {
            discountCodeId: string | null;
            codeName: string | null;
            note: string | null;
            totalAmount: number;
        }>();

        const fmtEur = (amount: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

        // First pass: collect all discount info.
        // A discount is treated as "manual" when it carries a note (notes are only ever
        // set for manual discounts); auto-applied code discounts have no note.
        for (const li of billingDraft.lineItems) {
            if (!li.discounts || li.discounts.length === 0) { continue; }
            for (const d of li.discounts) {
                const amount = toNum(d.discountAmountEur);
                const isManual = !!d.note || !d.discountCodeId;
                if (!isManual && d.discountCodeId && d.discountCode) {
                    const existing = discountCodeMap.get(d.discountCodeId);
                    if (existing) {
                        existing.totalAmount += amount;
                    } else {
                        discountCodeMap.set(d.discountCodeId, {
                            discountCodeId: d.discountCodeId,
                            label: d.discountCode.name,
                            type: d.discountCode.type,
                            value: toNum(d.discountCode.discountValue),
                            totalAmount: amount,
                        });
                    }
                } else {
                    const key = `${d.discountCodeId ?? ''}|${d.note ?? ''}`;
                    const existing = manualDiscountMap.get(key);
                    if (existing) {
                        existing.totalAmount += amount;
                    } else {
                        manualDiscountMap.set(key, {
                            discountCodeId: d.discountCodeId ?? null,
                            codeName: d.discountCode?.name ?? null,
                            note: d.note ?? null,
                            totalAmount: amount,
                        });
                    }
                }
            }
        }

        // Build discount definitions from collected info
        for (const [codeId, info] of discountCodeMap) {
            defs.push({
                id: `code-${codeId}`,
                source: 'code',
                discountCodeId: codeId,
                label: info.label,
                type: info.type === 'PERCENTAGE_DISCOUNT' ? 'PERCENTAGE' : 'ABSOLUTE',
                value: info.type === 'PERCENTAGE_DISCOUNT' ? info.value : info.totalAmount,
                targetAll: true,
                targetIndices: [],
                validBookableItemIds: [],
                validTreatmentVariantIds: [],
                validBundleIds: [],
                validProductIds: [],
            });
        }

        let manualIdx = 0;
        for (const info of manualDiscountMap.values()) {
            if (info.totalAmount <= 0) { continue; }
            const label = info.codeName
                ? `Manueller Rabatt · ${info.codeName} (${fmtEur(info.totalAmount)})`
                : `Manueller Rabatt (${fmtEur(info.totalAmount)})`;
            defs.push({
                id: `manual-restored-${manualIdx++}-${Date.now()}`,
                source: 'manual',
                discountCodeId: info.discountCodeId,
                label,
                type: 'ABSOLUTE',
                value: info.totalAmount,
                targetAll: true,
                targetIndices: [],
                validBookableItemIds: [],
                validTreatmentVariantIds: [],
                validBundleIds: [],
                validProductIds: [],
                note: info.note,
            });
        }

        // Map line items with BASE prices (totalGross is the original undiscounted value)
        const items: BillingDraftLineItem[] = billingDraft.lineItems.map((li) => {
            const baseGross = toNum(li.totalGross);
            const vatRate = toNum(li.vatRate);
            const baseNet = baseGross / (1 + vatRate);
            const baseVat = baseGross - baseNet;

            return {
                id: li.id,
                description: li.description,
                sequence: li.sequence,
                servicePillarType: li.servicePillarType as 'AESTHETIC' | 'COSMETIC' | 'HEALTH',
                serviceMedicalType: li.serviceMedicalType as 'MEDICAL' | 'NOT_MEDICAL',
                sourceType: li.sourceType as 'ORIGINATED_FROM_CUSTOMER_BOOKED_CART' | 'UPSELL' | 'PHYSICAL_PRODUCT' | 'BUNDLE_FIRST_CHARGE',
                totalGross: baseGross,
                totalGrossDiscounted: toNum(li.totalGrossDiscounted),
                grossDiscountAmount: toNum(li.grossDiscountAmount),
                vatRate,
                totalNet: baseNet,
                totalNetDiscounted: toNum(li.totalNetDiscounted),
                netDiscountAmount: toNum(li.netDiscountAmount),
                vatAmount: baseVat,
                vatAmountDiscounted: toNum(li.vatAmountDiscounted),
                treatmentVariantId: li.treatmentVariantId || null,
                physicalProductId: li.physicalProductId || null,
                bookableItemId: li.bookableItemId || null,
                bookableItemAddonId: li.bookableItemAddonId || null,
                cartItemSourceId: li.cartItemSourceId || null,
                bundleId: li.bundleId || null,
                consumeFirstCharge: li.consumeFirstCharge,
                paidByBundleVoucherId: null,
                originalPriceGross: undefined,
                giftCardTemplateId: li.giftCardTemplateId || null,
                giftCardRecipientCustomerId: li.giftCardRecipientCustomerId || null,
                discounts: [],
            };
        });

        return { mappedLineItems: items, initialDiscountDefs: defs };
    }, [billingDraft]);

    const form = useForm<BillingDraftFormData>({
        resolver: zodResolver(billingDraftFormSchema),
        defaultValues: {
            customerId: billingDraft.customerId,
            appointmentId: billingDraft.appointmentId || null,
            storeId: billingDraft.storeId,
            createdById: billingDraft.createdById,
            treatmentDate: formatDateStr(billingDraft.treatmentDate),
            billingDraftDate: formatDateStr(billingDraft.billingDraftDate),
            dueDate: formatDateStr(billingDraft.dueDate),
            type: billingDraft.type as 'NORMAL' | 'INFLUENCER' | 'MARKETING' | 'ACADEMY',
            influencerType: (billingDraft as any).influencerType ?? null,
            status: billingDraft.status as 'PENDING_PAYMENT' | 'PAID' | 'CANCELLED',
            companyName: billingDraft.companyName,
            companyAddress: billingDraft.companyAddress,
            companyCity: billingDraft.companyCity,
            companyCountry: billingDraft.companyCountry,
            customerName: billingDraft.customerName,
            customerNumber: billingDraft.customerNumber,
            customerStreet: billingDraft.customerStreet,
            customerZipcode: billingDraft.customerZipcode,
            customerCity: billingDraft.customerCity,
            customerCountry: billingDraft.customerCountry,
            cartSourceId: billingDraft.cartSourceId || null,
            lineItems: mappedLineItems,
        },
    });

    const { watch } = form;
    const lineItems = watch('lineItems');
    const treatmentDate = watch('treatmentDate');

    // Fetch valid discount codes for the treatment date
    const { data: validDiscountCodes } = trpc.billingDrafts.getValidDiscountCodes.useQuery(
        { treatmentDate },
        { enabled: !!treatmentDate }
    );

    // Fetch customer type (new vs returning) for discount code eligibility checks
    const { data: customerTypeData } = trpc.billingDrafts.getCustomerType.useQuery(
        { customerId: billingDraft.customerId },
        { enabled: billingDraft.customerId > 0 }
    );

    const totals = lineItems.reduce(
        (acc, item) => ({
            totalGross: acc.totalGross + item.totalGross,
            totalGrossDiscounted: acc.totalGrossDiscounted + item.totalGrossDiscounted,
            totalNet: acc.totalNet + item.totalNet,
            totalNetDiscounted: acc.totalNetDiscounted + item.totalNetDiscounted,
            vatAmount: acc.vatAmount + item.vatAmount,
            vatAmountDiscounted: acc.vatAmountDiscounted + item.vatAmountDiscounted,
        }),
        { totalGross: 0, totalGrossDiscounted: 0, totalNet: 0, totalNetDiscounted: 0, vatAmount: 0, vatAmountDiscounted: 0 }
    );

    // Update mutation
    const updateMutation = trpc.billingDrafts.update.useMutation({
        onSuccess: () => {
            toast.success('Auftrag wurde aktualisiert');
            trpcUtils.billingDrafts.getOne.invalidate({ id: billingDraft.id });
            trpcUtils.billingDrafts.getAll.invalidate();
            onSuccess();
            onOpenChange(false);
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const handleSubmit = form.handleSubmit((data) => {
        updateMutation.mutate({
            id: billingDraft.id,
            treatmentDate: data.treatmentDate,
            billingDraftDate: data.billingDraftDate,
            dueDate: data.dueDate,
            type: data.type,
            companyName: data.companyName,
            companyAddress: data.companyAddress,
            companyCity: data.companyCity,
            companyCountry: data.companyCountry,
            customerName: data.customerName,
            customerNumber: data.customerNumber,
            customerStreet: data.customerStreet,
            customerZipcode: data.customerZipcode,
            customerCity: data.customerCity,
            customerCountry: data.customerCountry,
            lineItems: data.lineItems,
        });
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='max-w-5xl max-h-[90vh] overflow-y-auto'>
                <DialogHeader>
                    <DialogTitle>Auftrag bearbeiten</DialogTitle>
                    <DialogDescription>
                        Bearbeiten Sie die Positionen und Grunddaten des Auftrags.
                    </DialogDescription>
                    {billingDraft.createdBy && (
                        <p className='text-sm text-muted-foreground'>
                            Erstellt von: {billingDraft.createdBy.firstName} {billingDraft.createdBy.lastName}
                        </p>
                    )}
                </DialogHeader>

                <div className='flex items-center gap-6 mb-4'>
                    <div className='flex items-center gap-3'>
                        <Label className='text-sm text-muted-foreground whitespace-nowrap'>Erstellt von:</Label>
                        <Select
                            value={String(selectedCreatorUserId)}
                            onValueChange={(v) => setSelectedCreatorUserId(Number(v))}
                        >
                            <SelectTrigger className='w-64 h-8'>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {usersData?.data.map((user) => (
                                    <SelectItem key={user.id} value={String(user.id)}>
                                        {user.firstName} {user.lastName}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className='flex items-center gap-3'>
                        <Label className='text-sm text-muted-foreground whitespace-nowrap'>Typ:</Label>
                        <Select
                            value={watch('type')}
                            onValueChange={(v) => {
                            form.setValue('type', v as 'NORMAL' | 'INFLUENCER' | 'MARKETING' | 'ACADEMY' | 'KULANZ');
                            form.setValue('lineItems', []);
                            if (v !== 'INFLUENCER') {
                                form.setValue('influencerType', null);
                            }
                        }}
                        >
                            <SelectTrigger className='w-48 h-8'>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value='NORMAL'>Standard</SelectItem>
                                <SelectItem value='INFLUENCER'>Influencer</SelectItem>
                                <SelectItem value='MARKETING'>Marketing</SelectItem>
                                <SelectItem value='ACADEMY'>Academy</SelectItem>
                                <SelectItem value='KULANZ'>Kulanz/Beschwerde</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {watch('type') === 'INFLUENCER' && (
                        <div className='flex items-center gap-3'>
                            <Label className='text-sm text-muted-foreground whitespace-nowrap'>Influencer-Typ:</Label>
                            <Select
                                value={watch('influencerType') || ''}
                                onValueChange={(v) => form.setValue('influencerType', v as any)}
                            >
                                <SelectTrigger className='w-64 h-8'>
                                    <SelectValue placeholder='Typ auswählen...' />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value='BEAUTY_INSIDER'>Beauty Insider</SelectItem>
                                    <SelectItem value='INFLUENCER'>Influencer Normal</SelectItem>
                                    <SelectItem value='INFLUENCER_FLATRATE'>Influencer Pauschalversteuerung</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className='grid w-full grid-cols-2'>
                        <TabsTrigger value='basic'>Grunddaten</TabsTrigger>
                        <TabsTrigger value='lineItems'>
                            Positionen ({lineItems.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value='basic' className='mt-6'>
                        <BillingDraftBasicDetailsForm
                            form={form}
                            preselectedCustomerId={billingDraft.customerId}
                            preselectedAppointmentId={billingDraft.appointmentId || undefined}
                        />
                    </TabsContent>

                    <TabsContent value='lineItems' className='mt-6'>
                        <BillingDraftLineItemsEditor
                            form={form}
                            totals={totals}
                            validDiscountCodes={validDiscountCodes ?? []}
                            initialDiscountDefinitions={initialDiscountDefs}
                            appointmentCouponCodes={appointmentData?.couponCodes}
                            appointmentStartTime={appointmentData?.steps?.[0]?.dateFrom ?? null}
                            customerType={customerTypeData?.customerType ?? null}
                        />
                    </TabsContent>
                </Tabs>

                <DialogFooter>
                    <div className='flex justify-between w-full'>
                        <Button variant='outline' onClick={() => onOpenChange(false)}>
                            Abbrechen
                        </Button>
                        <div className='flex gap-2'>
                            {activeTab === 'basic' && (
                                <Button onClick={() => setActiveTab('lineItems')}>
                                    Weiter zu Positionen
                                </Button>
                            )}
                            {activeTab === 'lineItems' && (
                                <>
                                    <Button variant='outline' onClick={() => setActiveTab('basic')}>
                                        Zurück
                                    </Button>
                                    <Button
                                        onClick={handleSubmit}
                                        disabled={updateMutation.isPending || lineItems.length === 0}
                                    >
                                        {updateMutation.isPending && (
                                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                        )}
                                        Änderungen speichern
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default BillingDraftEditor;
